import { CSSProperties, ReactNode } from 'react';
import { UI_BORDERS_PANEL_REGION } from '../../data/ui.atlas';
import { useNineSliceSource } from '../../hooks/useNineSliceSource';
import './NineSliceFrame.scss';

interface NineSliceRegion {
  image: string;
  x: number;
  y: number;
  size: number;
  // Source height in px. Defaults to `size` (square region).
  height?: number;
  cell: number;
}

interface Props {
  children?: ReactNode;
  className?: string;
  scale?: number;
  style?: CSSProperties;
  region?: NineSliceRegion;
}

/**
 * The 9-slice frame is a decorative `::before` overlay (`pointer-events: none`);
 * the container has no border layout, so content fills 100% of the box and the
 * frame artwork sits on top — add `padding` on your selector to keep content
 * clear of the edges. Until the canvas extraction finishes, `::before` is not
 * rendered so an absent frame does not flash.
 */
export const NineSliceFrame = ({
  children,
  className,
  scale = 3,
  style,
  region = UI_BORDERS_PANEL_REGION,
}: Props) => {
  const { image, x, y, size, height, cell } = region;
  const src = useNineSliceSource(image, x, y, size, height ?? size);
  const border = cell * scale;

  const frameVars = src
    ? ({
        '--ns-border': `${border}px`,
        '--ns-src': `url('${src}')`,
        '--ns-slice': cell,
      } as CSSProperties)
    : undefined;

  return (
    <div
      className={`nine-slice-frame${src ? ' is-ready' : ''}${className ? ` ${className}` : ''}`}
      style={{ ...frameVars, ...style }}
    >
      {children}
    </div>
  );
};
